import React, { useState, useEffect, useRef } from 'react';
import PageHeader from '../../components/ui/PageHeader';
import { classNames } from 'primereact/utils';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ColorPicker } from 'primereact/colorpicker';
import ActionToolbar from '../../components/ui/ActionToolbar';
import { CategoriaService } from '../../services/CategoriaService';
import { useAuth } from '../../context/AuthContext';

const Categorias = () => {
    const { user } = useAuth();
    let emptyCategoria = {
        id: null,
        nombre: '',
        color: 'f44336', // Default simple hex color without #
        icono: '-',
        idEmpresa: user?.idEmpresa || 1
    };

    const [categorias, setCategorias] = useState([]);
    const [categoriaDialog, setCategoriaDialog] = useState(false);
    const [deleteCategoriaDialog, setDeleteCategoriaDialog] = useState(false);
    const [categoria, setCategoria] = useState(emptyCategoria);
    const [selectedCategorias, setSelectedCategorias] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const toast = useRef(null);
    const dt = useRef(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const dataCat = await CategoriaService.getAll();
            setCategorias(dataCat.data || []);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las categorías', life: 3000 });
        }
    };

    const openNew = () => {
        setCategoria(emptyCategoria);
        setSubmitted(false);
        setCategoriaDialog(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setCategoriaDialog(false);
    };

    const hideDeleteCategoriaDialog = () => {
        setDeleteCategoriaDialog(false);
    };

    const saveCategoria = async () => {
        setSubmitted(true);
        if (categoria.nombre.trim()) {
            try {
                // Formatting color as full HEX string in case it doesn't have #
                let saveCat = { ...categoria };
                saveCat.color = saveCat.color.startsWith('#') ? saveCat.color : `#${saveCat.color}`;

                if (categoria.id) {
                    await CategoriaService.update(categoria.id, saveCat);
                    toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Categoría Actualizada', life: 3000 });
                } else {
                    await CategoriaService.create(saveCat);
                    toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Categoría Creada', life: 3000 });
                }
                setCategoriaDialog(false);
                setCategoria(emptyCategoria);
                loadData();
            } catch (error) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar la categoría', life: 3000 });
            }
        }
    };

    const editCategoria = (categoria) => {
        // Formatear hex si viene con # para el ColorPicker
        let formatCat = { ...categoria };
        if (formatCat.color && formatCat.color.startsWith('#')) {
            formatCat.color = formatCat.color.substring(1);
        }
        setCategoria(formatCat);
        setCategoriaDialog(true);
    };

    const confirmDeleteCategoria = (categoria) => {
        setCategoria(categoria);
        setDeleteCategoriaDialog(true);
    };

    const deleteCategoria = async () => {
        try {
            await CategoriaService.delete(categoria.id);
            setDeleteCategoriaDialog(false);
            setCategoria(emptyCategoria);
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Categoría Eliminada', life: 3000 });
            loadData();
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar la categoría', life: 3000 });
        }
    };

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _categoria = { ...categoria };
        _categoria[name] = val;
        setCategoria(_categoria);
    };

    const onColorChange = (e) => {
        let _categoria = { ...categoria };
        _categoria.color = e.value;
        setCategoria(_categoria);
    };

    const colorBodyTemplate = (rowData) => {
        let colorCode = rowData.color && rowData.color.startsWith('#') ? rowData.color : `#${rowData.color}`;
        return (
            <div className="flex align-items-center gap-2">
                <div style={{ backgroundColor: colorCode, width: '20px', height: '20px', borderRadius: '4px', border: '1px solid #dee2e6' }}></div>
                <span>{colorCode}</span>
            </div>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => editCategoria(rowData)} />
                <Button icon="pi pi-trash" rounded outlined onClick={() => confirmDeleteCategoria(rowData)} />
            </React.Fragment>
        );
    };



    const categoriaDialogFooter = (
        <React.Fragment>
            <Button label="Cancelar" icon="pi pi-times" outlined onClick={hideDialog} />
            <Button label="Guardar" icon="pi pi-check" onClick={saveCategoria} />
        </React.Fragment>
    );

    const deleteCategoriaDialogFooter = (
        <React.Fragment>
            <Button label="No" icon="pi pi-times" outlined onClick={hideDeleteCategoriaDialog} />
            <Button label="Sí, Eliminar" icon="pi pi-check" onClick={deleteCategoria} />
        </React.Fragment>
    );

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <PageHeader 
                    title="Gestión de Categorías" 
                    description="Clasifique y asigne colores a las categorías de sus productos." 
                    icon="pi pi-tags" 
                />
                <div className="card shadow-2 border-round-xl">
                    <Toast ref={toast} />
                    <ActionToolbar
                        onNew={openNew}
                        newLabel="Nueva Categoría"
                        onSearch={(val) => setGlobalFilter(val)}
                        searchValue={globalFilter}
                        searchPlaceholder="Buscar categoría..."
                        extraActions={null}
                    />

                    <DataTable ref={dt} value={categorias} selection={selectedCategorias} onSelectionChange={(e) => setSelectedCategorias(e.value)}
                        dataKey="id" paginator rows={10} rowsPerPageOptions={[5, 10, 25]} className="datatable-responsive p-datatable-sm"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} categorías"
                        globalFilter={globalFilter} emptyMessage="No se encontraron categorías." responsiveLayout="scroll">
                        <Column selectionMode="multiple" headerStyle={{ width: '4rem' }}></Column>
                        <Column field="nombre" header="Nombre" sortable headerStyle={{ minWidth: '15rem' }}></Column>
                        <Column field="color" header="Color" body={colorBodyTemplate} sortable headerStyle={{ minWidth: '8rem' }}></Column>
                        <Column body={actionBodyTemplate} headerStyle={{ minWidth: '10rem' }}></Column>
                    </DataTable>

                    <Dialog visible={categoriaDialog} style={{ width: '450px' }} header="Detalle de Categoría" modal className="p-fluid" footer={categoriaDialogFooter} onHide={hideDialog}>
                        <div className="field">
                            <label htmlFor="nombre">Nombre de la Categoría</label>
                            <InputText id="nombre" value={categoria.nombre} onChange={(e) => onInputChange(e, 'nombre')} required autoFocus className={classNames({ 'p-invalid': submitted && !categoria.nombre })} />
                            {submitted && !categoria.nombre && <small className="p-error">El nombre es requerido.</small>}
                        </div>
                        <div className="field">
                            <label htmlFor="color">Color Representativo</label>
                            <div className="flex align-items-center gap-3">
                                <ColorPicker id="color" value={categoria.color} onChange={onColorChange} />
                                <span>#{categoria.color}</span>
                            </div>
                        </div>
                    </Dialog>

                    <Dialog visible={deleteCategoriaDialog} style={{ width: '450px' }} header="Confirmar" modal footer={deleteCategoriaDialogFooter} onHide={hideDeleteCategoriaDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {categoria && <span>¿Está seguro que desea eliminar la categoría <b>{categoria.nombre}</b>?</span>}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default Categorias;
